import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavItem {
  id: number;
  label: string;
  path: string;
  visible: boolean;
}

export default function Navigation() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const [logoText, setLogoText] = useState('');
  const [logoFont, setLogoFont] = useState('Pacifico');
  const [logoType, setLogoType] = useState('text');
  const [logoImage, setLogoImage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch logo content from admin
  useEffect(() => {
    fetch('/admin/content-api.php')
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data) {
          if (data.data.site_logo) {
            setLogoText(data.data.site_logo);
          }
          if (data.data.site_logo_font) {
            setLogoFont(data.data.site_logo_font);
          }
          if (data.data.site_logo_type) {
            setLogoType(data.data.site_logo_type);
          }
          if (data.data.site_logo_image) {
            setLogoImage(data.data.site_logo_image);
          }
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.log('Could not fetch logo content:', error);
        setLogoText('Creative Duo');
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    // Fetch navigation items from API
    fetch('/admin/public-api.php?action=get_navigation')
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.data)) {
          setNavItems(data.data.filter((item: NavItem) => item.visible));
        }
      })
      .catch(err => {
        console.error('Error fetching navigation:', err);
        // Fallback to default navigation if API fails
        setNavItems([
          { id: 1, label: 'Work', path: '/work', visible: true },
          { id: 2, label: 'About', path: '/about', visible: true },
          { id: 3, label: 'Awards', path: '/awards', visible: true },
          { id: 4, label: 'Get In Touch', path: '/contact', visible: true }
        ]);
      });
  }, []);

  const isActive = (path: string) => {
    if (path === '/work') {
      return location.pathname === '/' || location.pathname === '/work' || location.pathname.startsWith('/project/');
    }
    return location.pathname === path;
  };

  const renderLogo = () => {
    if (isLoading) {
      return (
        <div className="h-8 w-32 bg-gray-100 animate-pulse rounded"></div>
      );
    }

    if (logoType === 'image' && logoImage) {
      return (
        <img 
          src={logoImage} 
          alt={logoText} 
          className="h-8 object-contain"
        />
      );
    }
    
    return (
      <span 
        className="text-2xl font-bold text-gray-900"
        style={{ fontFamily: `"${logoFont}", serif` }}
      >
        {logoText}
      </span>
    );
  };

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 z-50">
      <div className="px-8 py-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            {renderLogo()}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden text-gray-900 hover:text-gray-600 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-6 pt-6 border-t border-gray-100">
            <div className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-base font-medium transition-colors ${
                    isActive(item.path)
                      ? 'text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
